import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import DataTable from '../../../components/modern/DataTable/DataTable';
import PageLayout from '../../../components/modern/PageLayout/PageLayout';
import ActionButton from '../../../components/modern/ActionButton/ActionButton';
import { adminApi } from '../../../utils/api';
import './CityIndex.scss';
import { HiPencil, HiTrash } from 'react-icons/hi';

interface City {
  id: number;
  name: string;
  countryId: number;
  localDeliveryFee: number;
}

interface Country {
  id: number;
  name: string;
}

const CityIndex: React.FC = () => {
  const { t } = useTranslation(['cities', 'common']);
  const [cities, setCities] = useState<City[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchCities(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const fetchCities = async (page: number, size: number) => {
    try {
      setLoading(true);
      const response = await adminApi.call(`/city?page=${page}&pageSize=${size}`, 'GET');

      if (!response.success) {
        setError(response.error || t('messages.fetchError'));
        return;
      }

      setCities(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setError(t('messages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await adminApi.call('/country?page=1&pageSize=1000', 'GET');

      if (!response.success) {
        console.error('Error fetching countries:', response.error);
        return;
      }

      setCountries(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const getCountryName = (countryId: number) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : '-';
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await adminApi.call(`/city/${id}`, 'DELETE');

      if (!response.success) {
        setError(response.error || t('messages.deleteError'));
        return;
      }

      fetchCities(currentPage, pageSize);
    } catch (error) {
      console.error('Error deleting city:', error);
      setError(t('messages.deleteError'));
    }
  };

  const columns = [
    {
      key: 'id',
      title: t('fields.id'),
      sortable: true,
    },
    {
      key: 'name',
      title: t('fields.name'),
      sortable: true,
    },
    {
      key: 'countryId',
      title: t('fields.country'),
      sortable: true,
      render: (value: number) => getCountryName(value),
    },
    {
      key: 'localDeliveryFee',
      title: t('fields.localDeliveryFee'),
      sortable: true,
      render: (value: number) => value ? `${value.toFixed(2)}` : '0.00',
    },
    {
      key: 'actions',
      title: t('cities:actions.title'),
      render: (_: any, record: City) => (
        <div className="action-buttons">
          <Link to={`/cities/edit/${record.id}`}>
            <ActionButton
              variant="primary"
              size="sm"
              tooltip={t('cities:actions.edit')}
              icon={HiPencil}
            >
            </ActionButton>
          </Link>
          <ActionButton
            variant="danger"
            size="sm"
            tooltip={t('cities:actions.delete')}
            icon={HiTrash}
            onClick={() => handleDelete(record.id)}
          >
          </ActionButton>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <PageLayout title={t('title')} breadcrumbs={[{ label: t('title') }]}>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common:loading')}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('title')}
      breadcrumbs={[{ label: t('title') }]}
      headerActions={
        <Link to="/cities/create">
          <ActionButton variant="primary">
            {t('actions.create')}
          </ActionButton>
        </Link>
      }
      isIndexPage={true}
    >
      <div className="city-index">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        
        <DataTable
          data={cities}
          columns={columns}
          searchable
          paginationMode="server"
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50]}
          showPageSizeSelector={true}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </div>
    </PageLayout>
  );
};

export default CityIndex;
